import {
  Grid,
  Icon,
  Image,
  InlineStack,
  Popover,
  PopoverContent,
  PopoverContentHeader,
  PopoverTrigger,
  Strong,
  Text,
  TextLabel,
} from '@customerio/pluma-components/react';
import type { SqlImportDatabase } from '@/services/db/sql-import-database/entity';
import { toSqlDatabaseType, SQL_DATABASE_CATALOG } from '@/features/sql-sync';
import { isEmptyString } from '@/utils/string';

type DatabaseDetailsProps = {
  database: SqlImportDatabase;
  /** The BigQuery dataset location of the import; a BigQuery database is named by project and location. */
  location: string;
};

type DatabaseTitleProps = DatabaseDetailsProps;

function DatabaseTitle({ database, location }: DatabaseTitleProps) {
  if (!isEmptyString(database.name)) {
    return database.name;
  }
  return (
    <>
      <Strong>{database.project_id}</Strong> in <Strong>{location}</Strong>
    </>
  );
}

/** The database an import reads from, with its connection details on hover. */
export function DatabaseDetails({ database, location }: DatabaseDetailsProps) {
  const type = toSqlDatabaseType(database.type);
  const hasEndpoint = !isEmptyString(database.host) || database.port !== 0;

  return (
    <Popover shouldTriggerOnClick={false} shouldTriggerOnFocus shouldTriggerOnHover placement="top">
      <PopoverTrigger cursor="pointer" data-test-sql-import-item-database>
        <Icon name="database" color="subtle" mr="050" isInline />
        <DatabaseTitle database={database} location={location} />
      </PopoverTrigger>

      <PopoverContent>
        <InlineStack alignBlock="center" gap="100" mb="100">
          {type == null ? null : <Image src={SQL_DATABASE_CATALOG[type].imageSrc} size="icon-v2-lg" alt="" />}
          <PopoverContentHeader>
            <DatabaseTitle database={database} location={location} />
          </PopoverContentHeader>
        </InlineStack>

        {hasEndpoint ? (
          <Grid columns="auto auto" alignItems="stretch" columnGap="200" rowGap="050" mt="100">
            <TextLabel>Host</TextLabel>
            <TextLabel>Port</TextLabel>
            <Text>{database.host}</Text>
            <Text>{database.port}</Text>
          </Grid>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
